import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
  ISignUpResult,
} from "amazon-cognito-identity-js";
import { getConfig } from "../config";

let poolInstance: CognitoUserPool | null = null;

function getPool(): CognitoUserPool {
  if (!poolInstance) {
    const config = getConfig();
    poolInstance = new CognitoUserPool({
      UserPoolId: config.cognitoUserPoolId,
      ClientId: config.cognitoUserPoolClientId,
    });
  }
  return poolInstance;
}

/**
 * SRP auth -- the password itself never crosses the wire, even under TLS.
 *
 * `authenticateUser` can call back in more ways than success and failure: Cognito also raises
 * `newPasswordRequired` (a user in FORCE_CHANGE_PASSWORD, which any console-created user is), plus the
 * MFA and custom-challenge callbacks. Handling only the first two left the promise permanently unsettled
 * in those cases -- no resolve, no reject, so `finally` never ran and the sign-in button stayed stuck on
 * "Signing in…" forever with no error.
 *
 * None of these are reachable today (minors get Permanent: true passwords, and MFA is off), so each
 * rejects with a plain explanation rather than implementing a flow that has no way to be exercised. The
 * point is that the promise always settles.
 */
export function signIn(username: string, password: string): Promise<CognitoUserSession> {
  const user = new CognitoUser({ Username: username, Pool: getPool() });
  const authDetails = new AuthenticationDetails({ Username: username, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
      newPasswordRequired: () =>
        reject(new Error("This account needs a new password set before it can sign in. Please contact your coach.")),
      mfaRequired: () => reject(new Error("This account requires a verification code, which isn't supported yet.")),
      totpRequired: () => reject(new Error("This account requires an authenticator code, which isn't supported yet.")),
      customChallenge: () => reject(new Error("This account requires an extra verification step that isn't supported yet.")),
    });
  });
}

export function signOut(): void {
  getPool().getCurrentUser()?.signOut();
}

export interface SignUpWithInviteInput {
  name: string;
  email: string;
  password: string;
  role: "coach" | "family";
  inviteToken?: string;
}

/**
 * The pool's `signInAliases: { email: true }` (lib/auth-stack.ts) makes email an ALIAS you sign in
 * with, not the Cognito username itself -- Cognito's signUp rejects a Username that's shaped like an
 * email in that configuration ("Username cannot be of email format, since user pool is configured for
 * email alias"). So signUp needs its own opaque username; the `email` attribute below is what actually
 * makes email-based sign-in work afterward.
 */
function generateCognitoUsername(): string {
  return `user-${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
}

/**
 * Self-service signup (PRD §4.2 step 2). `custom:inviteToken`, when present, is what lets the
 * post-confirmation trigger link the resulting account back to the invite that authorized it
 * (lambda/auth/post-confirmation.ts) -- entirely server-side, no separate authenticated call needed
 * right after confirming.
 */
export function signUp(input: SignUpWithInviteInput): Promise<ISignUpResult> {
  const attributes = [
    new CognitoUserAttribute({ Name: "name", Value: input.name }),
    new CognitoUserAttribute({ Name: "email", Value: input.email }),
    new CognitoUserAttribute({ Name: "custom:role", Value: input.role }),
    ...(input.inviteToken ? [new CognitoUserAttribute({ Name: "custom:inviteToken", Value: input.inviteToken })] : []),
  ];

  return new Promise((resolve, reject) => {
    getPool().signUp(generateCognitoUsername(), input.password, attributes, [], (err, result) => {
      if (err || !result) {
        reject(err ?? new Error("Signup failed"));
        return;
      }
      resolve(result);
    });
  });
}

/**
 * Confirms the code Cognito emailed at signUp. Must be given the generated username from
 * `signUp`'s result (`result.user.getUsername()`), NOT the email address.
 *
 * This is the whole reason family signup was impossible. Email is an alias on this pool, and an alias
 * does not resolve to an UNCONFIRMED user -- it only becomes usable once the account is confirmed, which
 * is precisely what this call is trying to achieve. Passing the email here meant Cognito never found the
 * user being confirmed and answered CodeMismatchException, which reads as "you typed the code wrong".
 * So every code appeared invalid, resending produced another code that also appeared invalid, and
 * starting over just made another account nobody could confirm. Verified against the live pool: the same
 * code fails with the email and succeeds with the generated username.
 */
export function confirmSignUp(username: string, code: string): Promise<void> {
  const user = new CognitoUser({ Username: username, Pool: getPool() });
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

/** Same rule as confirmSignUp: the account is still unconfirmed, so the email alias cannot address it. */
export function resendConfirmationCode(username: string): Promise<void> {
  const user = new CognitoUser({ Username: username, Pool: getPool() });
  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

/**
 * Starts a password reset: Cognito emails a verification code to the address on the account.
 *
 * Deliberately resolves the same way whether or not the account exists. Cognito's own
 * `UserNotFoundException` would otherwise turn this screen into an account-existence oracle -- anyone
 * could type an address and learn whether that person is a client here, which for a service used by
 * minors is worth more than the small convenience of a precise error.
 *
 * A minor's login is username-based with no email attribute at all (architecture §2c), so a reset for
 * one has nowhere to send a code. Cognito reports that as InvalidParameterException, which is passed
 * through -- the guardian resets a minor's password for them, not the other way round.
 */
export function requestPasswordReset(username: string): Promise<void> {
  const user = new CognitoUser({ Username: username, Pool: getPool() });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err: Error & { code?: string }) => {
        if (err?.code === "UserNotFoundException") {
          resolve();
          return;
        }
        reject(err);
      },
    });
  });
}

/** Completes the reset with the emailed code and the new password. */
export function confirmPasswordReset(username: string, code: string, newPassword: string): Promise<void> {
  const user = new CognitoUser({ Username: username, Pool: getPool() });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err: Error) => reject(err),
    });
  });
}

/** Refreshes the session if needed and returns the current ID token (a JWT) -- what the API's JWT authorizer expects in Authorization. */
export function getIdToken(): Promise<string | null> {
  const user = getPool().getCurrentUser();
  if (!user) return Promise.resolve(null);

  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        resolve(null);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

export function currentUsername(): string | null {
  return getPool().getCurrentUser()?.getUsername() ?? null;
}

/**
 * Decodes the real `cognito:groups` claim straight from the ID token payload
 * (amazon-cognito-identity-js's own decode, not a round trip through API
 * Gateway). Unlike the server-side JWT authorizer -- which flattens this
 * claim into a Java-style "[coach]" string (see lambda/shared/caller.ts) --
 * the raw token payload is just JSON, so this is a normal array here.
 */
export async function isCoach(): Promise<boolean> {
  const user = getPool().getCurrentUser();
  if (!user) return false;

  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        resolve(false);
        return;
      }
      const groups = session.getIdToken().decodePayload()["cognito:groups"];
      resolve(Array.isArray(groups) && groups.includes("coach"));
    });
  });
}

/**
 * Who the signed-in user is, straight off the ID token.
 *
 * Same decode path as `isCoach`: the token is already in memory and already carries these claims, so
 * asking the API for them would be a round trip to learn something the client can read for free. Every
 * field is optional because a claim's presence depends on how the account was made -- a minor's login
 * has no email at all (PRD §12), and a coach account created by hand may not have a name set.
 */
export async function getCurrentUserClaims(): Promise<{ email: string | null; name: string | null; username: string | null }> {
  const user = getPool().getCurrentUser();
  if (!user) return { email: null, name: null, username: null };

  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        resolve({ email: null, name: null, username: null });
        return;
      }
      const claims = session.getIdToken().decodePayload();
      resolve({
        email: (claims["email"] as string | undefined) ?? null,
        name: (claims["name"] as string | undefined) ?? null,
        username: user.getUsername() ?? null,
      });
    });
  });
}

/**
 * Change your own password while signed in.
 *
 * The app had no way to do this at all: the only password change that existed was the emailed
 * forgot-password code, so an adult who simply wanted to rotate their password had to pretend to have
 * forgotten it. `changePassword` verifies the old one itself, which is why this takes both.
 *
 * A session is fetched first even though it looks redundant -- amazon-cognito-identity-js throws
 * "User is not authenticated" if `changePassword` is called before the user object has a live session,
 * which is the normal state on a freshly loaded page.
 */
export function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  const user = getPool().getCurrentUser();
  if (!user) return Promise.reject(new Error("You're not signed in."));

  return new Promise((resolve, reject) => {
    user.getSession((sessionErr: Error | null, session: CognitoUserSession | null) => {
      if (sessionErr || !session) {
        reject(sessionErr ?? new Error("Your session has expired. Sign in again."));
        return;
      }
      user.changePassword(oldPassword, newPassword, (err) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  });
}

/** The current signed-in identity's cognito sub -- used client-side to tell "my own message" apart from others' in a thread (Phase 4 messaging), same decode path as isCoach(). */
export async function getCurrentCognitoSub(): Promise<string | null> {
  const user = getPool().getCurrentUser();
  if (!user) return null;

  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        resolve(null);
        return;
      }
      resolve((session.getIdToken().decodePayload()["sub"] as string | undefined) ?? null);
    });
  });
}
